import { apiClient, API_PATHS } from '@/services/api';

class AlertService {

  /**
   * Get alerts for a sponsor
   */
  async getAlerts(limit = 50) {
    try {
      const response = await apiClient.alert.get(API_PATHS.alerts.list, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      throw new Error('Failed to fetch alerts');
    }
  }

  /**
   * Mark alert as read
   * @param {string} alertId - The ID of the alert to mark as read
   * @returns {Promise<Object>} - Response object
   */
  async markAsRead(alertId) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.read(alertId), {});
      return response.data;
    } catch (error) {
      console.error('Error marking alert as read:', error);
      throw error;
    }
  }

  /**
   * Delete alert (mark user's alert status as deleted)
   * @param {string} alertId - The ID of the alert to mark as deleted for the current user
   * @returns {Promise<Object>} - Response object
   */
  async deleteAlert(alertId) {
    try {
      // Mark the user's alert status as deleted, not the alert itself
      const response = await apiClient.alert.post(API_PATHS.alerts.delete(alertId), {});
      return response.data;
    } catch (error) {
      console.error('Error marking alert as deleted:', error);
      throw error;
    }
  }

  /**
   * Mark all alerts as read
   */
  async markAllAsRead() {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.readAll, {});
      return response.data;
    } catch (error) {
      console.error('Failed to mark all alerts as read:', error);
      throw new Error('Failed to mark all alerts as read');
    }
  }

  /**
   * Create a new alert
   * @param {Object} alertData - The alert data to create
   * @returns {Promise<Object>} - Response object
   */
  async createAlert(alertData) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.list, alertData);
      return response.data;
    } catch (error) {
      console.error('Failed to create alert:', error);
      throw error;
    }
  }

  /**
   * Bulk create alerts: one alert per user group (partner)
   * @param {Object} payload - Base alert fields + userGroups array
   * @param {string[]} payload.userGroups - List of partner user group identifiers
   * @returns {Promise<{ success: boolean, data: { createdCount: number, ids: string[] } }>}
   */
  async createAlertsBulk(payload) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.bulk, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to bulk create alerts:', error);
      const message = error?.response?.data?.error || error.message || 'Failed to bulk create alerts';
      throw new Error(message);
    }
  }

  /**
   * Get alert details by ID
   * @param {string} alertId - The ID of the alert
   * @returns {Promise<Object>} - Response object
   */
  async getAlertById(alertId) {
    try {
      const response = await apiClient.alert.get(API_PATHS.alerts.byId(alertId));
      return response.data;
    } catch (error) {
      console.error('Failed to fetch alert details:', error);
      throw error;
    }
  }

  /**
   * Get unread count
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.alert.get(API_PATHS.alerts.unreadCount);
      return response.data;
    } catch (error) {
      console.error('Failed to get unread count:', error);
      throw new Error('Failed to get unread count');
    }
  }

  /**
   * Get specific alert details
   */
  async getAlert(alertId) {
    try {
      const response = await apiClient.alert.get(API_PATHS.alerts.byId(alertId));
      return response.data;
    } catch (error) {
      console.error('Failed to get alert:', error);
      throw new Error('Failed to get alert');
    }
  }

  /**
   * Acknowledge alert (for urgent alerts)
   */
  async acknowledgeAlert(alertId) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.acknowledge(alertId), {});
      return response.data;
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      throw new Error('Failed to acknowledge alert');
    }
  }

  /**
   * Dismiss alert
   */
  async dismissAlert(alertId) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.dismiss(alertId), {});
      return response.data;
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
      throw new Error('Failed to dismiss alert');
    }
  }

  /**
   * Get alerts with pagination
   */
  async getAlertsPaginated(params = {}) {
    try {
      // Filter out undefined/null values and build params object
      const filteredParams = {};
      if (params.limit !== undefined) filteredParams.limit = params.limit;
      if (params.offset !== undefined) filteredParams.offset = params.offset;
      if (params.sortBy) filteredParams.sortBy = params.sortBy;
      if (params.sortOrder) filteredParams.sortOrder = params.sortOrder;
      if (params.type) filteredParams.type = params.type;
      if (params.priority) filteredParams.priority = params.priority;
      if (params.readingStatus) filteredParams.readingStatus = params.readingStatus;
      if (params.startDate) filteredParams.startDate = params.startDate;
      if (params.endDate) filteredParams.endDate = params.endDate;

      const response = await apiClient.alert.get(API_PATHS.alerts.list, {
        params: filteredParams
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch paginated alerts:', error);
      throw new Error('Failed to fetch paginated alerts');
    }
  }

  /**
   * Update alert settings
   */
  async updateAlertSettings(settings) {
    try {
      const response = await apiClient.alert.put(API_PATHS.alerts.settings, settings);
      return response.data;
    } catch (error) {
      console.error('Failed to update alert settings:', error);
      throw new Error('Failed to update alert settings');
    }
  }

  /**
   * Get alert settings
   */
  async getAlertSettings() {
    try {
      const response = await apiClient.alert.get(API_PATHS.alerts.settings);
      return response.data;
    } catch (error) {
      console.error('Failed to get alert settings:', error);
      throw new Error('Failed to get alert settings');
    }
  }

  /**
   * Admin: Create a new alert
   * Note: Backend determines default fields like id/status/createdAt
   * @param {Object} payload - Alert creation payload (type, title, message, priority, metadata, targeting, etc.)
   * @returns {Promise<Object>} - Created alert response
   */
  async createAlertByAdmin(payload) {
    try {
      const response = await apiClient.alert.post(API_PATHS.alerts.admin.create, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to create alert by admin:', error);
      throw new Error('Failed to create alert by admin');
    }
  }

  /**
   * Admin: Update an existing alert
   * @param {string} alertId - The alert ID to update
   * @param {Object} updates - Fields to update (title, message, priority, metadata, targeting, status, etc.)
   * @returns {Promise<Object>} - Updated alert response
   */
  async updateAlertByAdmin(alertId, updates) {
    try {
      const response = await apiClient.alert.put(API_PATHS.alerts.admin.update(alertId), updates);
      return response.data;
    } catch (error) {
      console.error('Failed to update alert by admin:', error);
      throw new Error('Failed to update alert by admin');
    }
  }

  /**
   * Admin: Delete an alert (hard delete or mark deleted depending on backend)
   * @param {string} alertId - The alert ID to delete
   * @returns {Promise<Object>} - Delete operation response
   */
  async deleteAlertByAdmin(alertId) {
    try {
      const response = await apiClient.alert.delete(API_PATHS.alerts.admin.delete(alertId));
      return response.data;
    } catch (error) {
      console.error('Failed to delete alert by admin:', error);
      throw new Error('Failed to delete alert by admin');
    }
  }
}

export const alertService = new AlertService();
export default alertService;